import React, { useState } from 'react';
import { FileText, Send, ExternalLink, Pencil, X } from 'lucide-react';
import { PageHeader } from './MassTimes';
import EditableText from '../components/EditableText';
import { HoneypotField } from '../components/HoneypotField';
import { useAuth } from '../context/AuthContext';
import { useContent } from '../context/ContentContext';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';

const RULES_URL_KEY = 'hall_rental_rules_url';
const RULES_LABEL_KEY = 'hall_rental_rules_label';

const FEATURES_DEFAULT = `Our parish hall is a warm and welcoming space, perfect for family celebrations, community gatherings, meetings, and parish events. Common features include:

• Seating capacity for up to 100 guests
• Fully equipped kitchen with commercial appliances
• Round tables and chairs available
• Sound system and projector on request
• Level access and accessible washrooms
• Ample parking on-site
• Wi-Fi available throughout the hall`;

const COST_DEFAULT = `Rental fees are set based on the type of event, duration, and whether kitchen facilities are required.

Contact the parish office for a current rate sheet and available dates. A refundable damage deposit is required to confirm the booking.`;

const EVENT_TYPES = [
  'Birthday Party',
  'Anniversary',
  'Baptism Reception',
  'Funeral Reception',
  'Wedding Reception',
  'Community Meeting',
  'Fundraiser',
  'Other',
];

// Small helper: admin-editable link block (URL + label stored in the CMS).
const RulesLink = () => {
  const { isAdmin } = useAuth();
  const { getContent, saveContent } = useContent();
  const url = getContent(RULES_URL_KEY, '');
  const label = getContent(RULES_LABEL_KEY, 'Hall Rental Rules & Regulations');
  const [editing, setEditing] = useState(false);
  const [urlDraft, setUrlDraft] = useState(url);
  const [labelDraft, setLabelDraft] = useState(label);
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);

  const isExternal = url && /^https?:\/\//i.test(url);

  const openEditor = () => {
    setUrlDraft(url);
    setLabelDraft(label);
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveContent(RULES_URL_KEY, urlDraft.trim());
      await saveContent(RULES_LABEL_KEY, labelDraft.trim());
      toast({ title: 'Rules link updated' });
      setEditing(false);
    } catch (err) {
      toast({ title: 'Save failed', description: String(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 mb-4 relative group" data-testid="hall-rental-rules">
      {url ? (
        <a
          href={url}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
          className="inline-flex items-center gap-2 px-5 py-3 border border-[#3a2359] bg-white text-[#3a2359] hover:bg-[#3a2359] hover:text-white text-sm font-medium"
          data-testid="hall-rental-rules-link"
        >
          <FileText size={16} />
          {label}
          {isExternal && <ExternalLink size={14} />}
        </a>
      ) : isAdmin ? (
        <div className="p-4 bg-[#faf7f0] border border-dashed border-[#c8b785] text-sm text-[#78716c] italic">
          Add a link to your Hall Rental rules (PDF or webpage) — visible only when set.
        </div>
      ) : null}
      {isAdmin && (
        <button
          type="button"
          onClick={openEditor}
          className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center justify-center w-7 h-7 border border-[#3a2359] bg-white text-[#3a2359] hover:bg-[#3a2359] hover:text-white"
          title="Edit rules link"
          data-testid="hall-rental-edit-rules-link"
        >
          <Pencil size={12} />
        </button>
      )}

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(false)} />
          <form onSubmit={save} className="relative bg-white border border-[#e9e0cc] w-full max-w-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e9e0cc]">
              <h3 className="font-display text-xl text-[#2a1a45]">Rules Link</h3>
              <button type="button" onClick={() => setEditing(false)} className="text-[#5a534a] hover:text-[#3a2359]"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#78716c] mb-1">Button label</label>
                <input
                  type="text"
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  data-testid="hall-rental-rules-label-input"
                  className="w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#78716c] mb-1">URL (PDF or webpage)</label>
                <input
                  type="url"
                  value={urlDraft}
                  onChange={(e) => setUrlDraft(e.target.value)}
                  placeholder="https://example.com/hall-rental-rules.pdf"
                  data-testid="hall-rental-rules-url-input"
                  className="w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]"
                />
                <p className="text-xs text-[#78716c] mt-2">Tip: upload a PDF via the Admin → Documents section, then paste the "download" URL here.</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-[#e9e0cc]">
              <button type="button" onClick={() => setEditing(false)} className="px-4 py-2 border border-[#e9e0cc] text-sm text-[#5a534a] hover:bg-[#faf7f0]">Cancel</button>
              <button type="submit" disabled={saving} className="px-5 py-2 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] disabled:opacity-60">
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const HallRentalForm = () => {
  const { toast } = useToast();
  const [values, setValues] = useState({
    name: '', email: '', phone: '', event_date: '', event_type: '', guest_count: '', message: '', website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setValues(v => ({ ...v, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name || !values.email || !values.message) {
      toast({ title: 'Please fill in name, email, and message', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await api.createHallRental(values);
      setSubmitted(true);
      toast({ title: 'Request sent', description: 'The parish office has received your rental request and will be in touch soon.' });
    } catch (err) {
      toast({ title: 'Could not send', description: err?.response?.data?.detail || String(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#f2ead6]/70 border border-[#e9e0cc] p-8 text-center" data-testid="hall-rental-submitted">
        <h3 className="font-display text-2xl text-[#2a1a45] mb-2">Thank you!</h3>
        <p className="text-[#5a534a] max-w-md mx-auto">
          Your Hall Rental request has been sent to the parish office. Someone will be in touch shortly to confirm availability and next steps.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="hall-rental-form">
      <HoneypotField value={values.website} onChange={(v) => setValues(s => ({ ...s, website: v }))} />
      <label className="block">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Your name <span className="text-[#8a2b1a]">*</span></span>
        <input type="text" required value={values.name} onChange={update('name')} data-testid="hall-rental-name" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Email <span className="text-[#8a2b1a]">*</span></span>
        <input type="email" required value={values.email} onChange={update('email')} data-testid="hall-rental-email" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Phone</span>
        <input type="tel" value={values.phone} onChange={update('phone')} data-testid="hall-rental-phone" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Preferred date</span>
        <input type="date" value={values.event_date} onChange={update('event_date')} data-testid="hall-rental-date" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]" />
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Event type</span>
        <select value={values.event_type} onChange={update('event_type')} data-testid="hall-rental-type" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359] bg-white">
          <option value="">Select an option…</option>
          {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="block">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Approx. guest count</span>
        <input type="number" min="1" value={values.guest_count} onChange={update('guest_count')} data-testid="hall-rental-guests" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]" />
      </label>
      <label className="block md:col-span-2">
        <span className="text-xs uppercase tracking-wider text-[#78716c]">Message / details <span className="text-[#8a2b1a]">*</span></span>
        <textarea required rows={5} value={values.message} onChange={update('message')} data-testid="hall-rental-message" className="mt-1 w-full border border-[#e9e0cc] px-3 py-2 text-sm focus:outline-none focus:border-[#3a2359]" />
      </label>
      <div className="md:col-span-2 flex items-center justify-between gap-4 pt-2">
        <p className="text-xs text-[#78716c]">Your request will be sent to the parish office.</p>
        <button type="submit" disabled={submitting} data-testid="hall-rental-submit" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3a2359] text-white text-sm hover:bg-[#4d2f75] disabled:opacity-60">
          <Send size={14} /> {submitting ? 'Sending…' : 'Send Request'}
        </button>
      </div>
    </form>
  );
};

const HallRental = () => {
  return (
    <>
      <PageHeader
        eyebrow="Parish Life"
        title="Hall Rental"
        subtitle="Book our parish hall for family celebrations, community events, and meetings."
      />

      <section className="max-w-4xl mx-auto px-6 py-14 space-y-14">
        {/* Features */}
        <div>
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">The Hall</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45] mb-4">Features &amp; Amenities</h2>
          <EditableText
            contentKey="hall_rental_features"
            label="Hall Rental — Features"
            defaultValue={FEATURES_DEFAULT}
            rows={10}
            paragraphClass="text-[15px] text-[#3f3a34] leading-[1.85]"
          />
        </div>

        {/* Cost / Rental Rate — placed before Rules per parish preference */}
        <div>
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">Rates</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45] mb-4">Rental Rate</h2>
          <EditableText
            contentKey="hall_rental_cost"
            label="Hall Rental — Rental Rate"
            defaultValue={COST_DEFAULT}
            rows={6}
            paragraphClass="text-[15px] text-[#3f3a34] leading-[1.85]"
          />
        </div>

        {/* Rules link */}
        <div>
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">Rules</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45] mb-3">Hall Rental Rules</h2>
          <p className="text-[15px] text-[#3f3a34] leading-relaxed">
            Before booking, please review the rules and expectations for using the parish hall.
          </p>
          <RulesLink />
        </div>

        {/* Rental Form */}
        <div>
          <p className="text-sm tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">Get in Touch</p>
          <h2 className="font-display text-3xl md:text-4xl text-[#2a1a45] mb-4">Hall Rental Request Form</h2>
          <p className="text-[15px] text-[#3f3a34] leading-relaxed mb-6">
            Fill out the form below and our parish office will get back to you to confirm availability and next steps.
          </p>
          <HallRentalForm />
        </div>
      </section>
    </>
  );
};

export default HallRental;
